import * as React from 'react';

import {
  ConsolidatedBindingContext,
  getElementBindingContext,
} from '@packages/systems/dynamo/gateway';
import {
  getTypeDependentElements,
  getHumanTexts,
  HumanTexts,
} from './ParamPanelUtils';
import {type DynQueryParamType} from './DynQueryParam';
import type {CollectionStateType} from '@packages/systems/core/records/CollectionStateType';
import type {LocalizationState} from '@packages/systems/localization-types';
import type {BetaFeatureStateType} from '@packages/systems/designer/beta-features';
import {Component} from '@packages/systems/wfdl';
import type {PageRecordType} from '@packages/systems/core/records/PageRecord';

export function useHumanTexts(
  type: 'query' | 'condition',
  dynQueryParam: DynQueryParamType,
  selectedNodeNativeId: string | null,
  currentComponent: Component.ComponentType,
  componentMap: Component.ComponentMap,
  currentPage: PageRecordType | null,
  collectionState: CollectionStateType,
  localizationState: LocalizationState,
  betaFeatureState: BetaFeatureStateType,
  isReferencedItemLoading: boolean
): HumanTexts {
  const {collections} = collectionState;

  const {element, dynamoItem, dynamoWrapper} = getTypeDependentElements({
    type,
    selectedNodeNativeId,
    currentComponent,
  });

  /*
    Collection list filters are essentially DynamoWrapper settings, but
    they are visible and configurable on DynamoWrapper, DynamoList,
    DynamoItem and Pagination elements.

    `getElementBindingContext` finds a collection list's binding context
    always from its DynamoItem element, which means we need to pass the
    DynamoItem of the closest DynamoWrapper to find the binding context
    that should be used for configuring collection list filters.

    For conditional visibility configuration, we can always pass the
    selected element to `getElementBindingContext`.
  */
  const contextElement = type === 'query' ? dynamoItem : element;

  const bindingContext = React.useMemo(() => {
    return contextElement
      ? getElementBindingContext(
          contextElement.id,
          currentComponent,
          componentMap,
          currentPage,
          collections
        )
      : null;
  }, [
    contextElement,
    currentComponent,
    componentMap,
    currentPage,
    collections,
    betaFeatureState,
  ]);

  const parentBindingContext = React.useMemo(() => {
    if (!bindingContext || !dynamoWrapper) {
      return null;
    }

    return dynamoWrapper
      ? getElementBindingContext(
          dynamoWrapper.id,
          currentComponent,
          componentMap,
          currentPage,
          collections
        )
      : null;
  }, [
    bindingContext,
    dynamoWrapper,
    currentComponent,
    componentMap,
    currentPage,
    collections,
    betaFeatureState,
  ]);

  return useHumanTextsWithBindingContexts(
    dynQueryParam,
    bindingContext,
    parentBindingContext,
    collectionState,
    localizationState,
    isReferencedItemLoading
  );
}

export function useHumanTextsWithBindingContexts(
  dynQueryParam: DynQueryParamType,
  bindingContext: ConsolidatedBindingContext | null,
  parentBindingContext: ConsolidatedBindingContext | null,
  collectionState: CollectionStateType,
  localizationState: LocalizationState,
  isReferencedItemLoading: boolean
): HumanTexts {
  const humanTexts = React.useMemo(() => {
    if (isReferencedItemLoading) {
      return {
        fieldText: null,
        operatorText: null,
        valueText: null,
        operatorBoundSymbol: '',
        operatorNonBoundSymbol: '',
      };
    }

    return bindingContext
      ? {
          ...getHumanTexts({
            bindingContext,
            parentBindingContext,
            dynQueryParam,
            collectionState,
            localizationState,
          }),
        }
      : {
          fieldText: null,
          operatorText: null,
          valueText: null,
          operatorBoundSymbol: '',
          operatorNonBoundSymbol: '',
        };
  }, [
    bindingContext,
    parentBindingContext,
    dynQueryParam,
    collectionState,
    localizationState,
    isReferencedItemLoading,
  ]);

  return humanTexts;
}
